import { forwardRef, useImperativeHandle } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';

import { GlobalAccount } from '@ndvr/customer-api';

import { MinimalTiptapEditor } from '@/components/minimal-tiptap';
import { Button } from '@/components/ui/button';
import { ComboboxOption, FormCombobox } from '@/components/ui/combobox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { FormInput } from '@/components/ui/input';
import { getHouseholdName } from 'utils';
import { useCommandCenterHouseholds } from 'views/household/household-queries';

export type HouseholdNoteFormType = {
    subject: string;
    content: string;
    householdOption?: ComboboxOption;
};

interface HouseholdNoteFormProps {
    globalAccount?: GlobalAccount;
    onSubmit: (data: HouseholdNoteFormType) => void;
    onCancel: () => void;
    defaultValues?: Partial<HouseholdNoteFormType>;
    showHouseholdInput?: boolean;
}

export type HouseholdNoteFormRef = {
    formMethods: UseFormReturn<HouseholdNoteFormType>;
};

export const HouseholdNoteForm = forwardRef<HouseholdNoteFormRef, HouseholdNoteFormProps>((props, ref) => {
    const { data: households = [] } = useCommandCenterHouseholds();

    const formMethods = useForm<HouseholdNoteFormType>({
        defaultValues: props.defaultValues,
    });

    useImperativeHandle(ref, () => ({ formMethods }), [formMethods]);

    return (
        <Form {...formMethods}>
            <form className="flex flex-col gap-4" onSubmit={formMethods.handleSubmit(props.onSubmit)}>
                {(props.showHouseholdInput ?? true) && (
                    <FormCombobox
                        formMethods={formMethods}
                        name="householdOption"
                        options={
                            households.map((household) => ({
                                value: household.globalAccountGUID,
                                label: getHouseholdName(household) || 'Unnamed Household',
                            })) satisfies ComboboxOption[]
                        }
                        label="Household*"
                        selectOptionLabel="Select a household"
                        noOptionsLabel="No households found"
                        placeholder="Select a household"
                        disabled={!!props.globalAccount}
                        required
                    />
                )}
                <FormInput formMethods={formMethods} name="subject" label="Subject" placeholder="Enter note subject" />
                <FormField
                    control={formMethods.control}
                    name="content"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Content</FormLabel>
                            <FormControl className="mt-3">
                                <MinimalTiptapEditor
                                    value={field.value || ''}
                                    onChange={field.onChange}
                                    className="w-full"
                                    editorContentClassName="p-5"
                                    output="html"
                                    placeholder="Enter note content here..."
                                    autofocus={true}
                                    editable={true}
                                    editorClassName="focus:outline-hidden"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <div className="flex justify-end gap-2">
                    <Button type="button" onClick={props.onCancel} variant="outline">
                        Cancel
                    </Button>
                    <Button type="submit">Save Note</Button>
                </div>
            </form>
        </Form>
    );
});

HouseholdNoteForm.displayName = 'HouseholdNoteForm';
